import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** A contact is "in use" once it carries an email or a phone; a name alone doesn't count. */
export function contactIsUsed(group: AbstractControl | null | undefined): boolean {
  return !!group && ['email', 'phone'].some((field) => !!group.get(field)?.value?.trim());
}

/**
 * RP-4072: a contact that holds ANYTHING must hold a name — you can't fill in just a phone.
 *
 * Conditional on purpose. A wholly empty contact is legitimate on the tabs that use this
 * (a charity need not have a website or social-media contact, a partner need not have a
 * billing one) and those forms drop it before sending via
 * UtilityService.removeNestedObjectWithAllEmptyValues — so a plain Validators.required
 * would leave such a record unable to save at all. It mirrors the rule the API already
 * declares and never enforces: Embedded::Contact `validates :name, presence: true`, which
 * attr_json does not propagate to the parent record.
 *
 * Trims, so a name of spaces does not satisfy it (Validators.required alone would).
 *
 * Put the validator on the NAME control. It reads its SIBLINGS off the parent group, and
 * Angular caches a control's validity until something re-runs it — so typing a phone into
 * an empty contact has to re-check that contact's name. That happens through the template's
 * `[required]="contactIsUsed(...)"` binding: flipping it makes Angular's own RequiredValidator
 * fire registerOnValidatorChange, which revalidates the control and re-runs this rule with
 * it. Keep that binding DYNAMIC — a static `required` attribute would freeze the rule at
 * whatever it was on first render.
 */
export function contactNameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!contactIsUsed(control.parent)) return null;
    return control.value?.trim() ? null : { required: true };
  };
}
